import { inject, Injectable } from '@angular/core';
import { logger, ChangeTyped, IndexedService, SearchResult, TEXTS_STORE } from '@damofire/data-commons';
import { LockingIndexedService } from '../indexed/locking-indexed.service';
import { LayoutTextSegment } from '../warehouse/warehouse.dto';
import { generateAndAssignTextIds, WarehouseLayoutTextService } from './warehouse-layout-text.service';
import { AuthDtoSignerService } from '@damofire/data-security';
import { WarehouseSyncEventService } from '../sync/warehouse-sync-event.service';
import { ServiceEventType } from '@damofire/data-angular-commons';

@Injectable()
export class IdbWarehouseLayoutTextService extends WarehouseLayoutTextService {
  private readonly dtoSigner: AuthDtoSignerService = inject(AuthDtoSignerService);
  private readonly warehouseSyncEventService: WarehouseSyncEventService = inject(WarehouseSyncEventService);
  private readonly idbService: IndexedService = inject(IndexedService);
  private readonly lockingIdbService: LockingIndexedService = inject(LockingIndexedService);

  async search(divisionId: string): Promise<SearchResult<LayoutTextSegment>> {
    const items = await this.lockingIdbService.getAllFromIndex(divisionId, TEXTS_STORE, 'division', divisionId);
    return { result: items as LayoutTextSegment[], page: { size: items.length } };
  }

  async create(divisionId: string, segments: LayoutTextSegment[]): Promise<void> {
    if (!segments.length) {
      return;
    }

    const textWithIds: LayoutTextSegment[] = generateAndAssignTextIds(segments);

    for (const segment of textWithIds) {
      await this.dtoSigner.sign(segment, true);
      await this.idbService.add(TEXTS_STORE, { ...segment, divisionId, division: { _id: divisionId } });

      const changeTyped: ChangeTyped<LayoutTextSegment> = { ...segment, changeType: 'added' };
      this.warehouseSyncEventService.publishOutput({ division: divisionId, changes: [changeTyped], store: TEXTS_STORE });
      super.publishEvent(ServiceEventType.CREATED, segment);
    }
  }

  async delete(divisionId: string, segments: LayoutTextSegment[]): Promise<void> {
    if (!segments.length) {
      return;
    }

    const changes: ChangeTyped<LayoutTextSegment>[] = [];
    for (const segment of segments) {
      await this.idbService.delete(TEXTS_STORE, [divisionId, segment._id]);

      changes.push({ ...segment, changeType: 'removed' });
    }

    logger.info('Layout texts deleted.', { count: segments.length, category: 'idb' });
    super.publishEvent(ServiceEventType.DELETED, ...segments);
    this.warehouseSyncEventService.publishOutput({ division: divisionId, changes, store: TEXTS_STORE });
  }

  async update(divisionId: string, segments: LayoutTextSegment[]): Promise<void> {
    if (!segments.length) {
      return;
    }

    for (const segment of segments) {
      if (segment._id) {
        await this.dtoSigner.sign(segment, false, false);
        await this.idbService.update(TEXTS_STORE, { ...segment, divisionId });

        const changeTyped: ChangeTyped<LayoutTextSegment> = { ...segment, changeType: 'modified' };
        this.warehouseSyncEventService.publishOutput({ division: divisionId, changes: [changeTyped], store: TEXTS_STORE });
      }
    }
  }
}
